import { useSelector } from 'react-redux';

import { TargetingValues } from '../api/openapi_gen/deliveryProviderPortal/deliveryProviderPortal/apis';
import { useCommonAppServices } from '../services/ContextProviders';
import { useTargetingParameter } from '../services/TargetingService';

/**
 * Duplicate selectors: circular dependencies on import
 * @see file://./../../../app/src/rdx/targetingParameters/reducer.ts
 */
interface TargetingParametersState {
  parameters: TargetingValues | null;
}

interface FauxGlobalAppState {
  targetingParameters: TargetingParametersState;
}

/**
 * Duplicate interfaces: circular dependencies on import
 * @see file://./../../../app/src/rdx/targetingParameters/selectors.ts
 */
function selectTargetingParameters(state: FauxGlobalAppState): TargetingValues {
  return (
    state.targetingParameters.parameters || {
      provider_onboarding: {},
      delivery_provider_portal_flags: {},
    }
  );
}

export function useTargetingParameters(): TargetingValues {
  try {
    return useSelector(selectTargetingParameters);
  } catch (error) {
    if (process.env.REACT_APP_STAGE !== 'live') {
      throw new Error('Check Observable variant');
    }

    throw error;
  }
}

export function useTargetingParametersObservable<
  T extends keyof TargetingValues,
  K extends keyof TargetingValues[T],
  V extends TargetingValues[T][K],
  R = TargetingValues[T][K],
>(targetingEventName: T, testOrFlagName: K, controlValue: V): R {
  const commonAppServices = useCommonAppServices();
  if (commonAppServices === null) {
    if (process.env.REACT_APP_STAGE !== 'live') {
      throw new Error('Check NonObservable variant');
    }

    throw new Error('Context requires a hydrated <Provider>');
  }

  return useTargetingParameter(
    testOrFlagName,
    commonAppServices.targetingService,
    (value) => value ?? controlValue,
    targetingEventName,
  );
}
